/**
 * 🏗️ AffiliateLayout - Main App Shell für das Affiliate Portal
 * 
 * Features:
 * - Responsive Sidebar (collapsible auf Desktop, Drawer auf Mobile)
 * - Topbar mit Breadcrumbs + Actions
 * - Content Area mit max-width
 * - Mobile-optimierte Navigation
 */

import React, { useState, useCallback } from 'react';
import { useLocation } from 'wouter';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/use-auth';
import { AffiliateSidebar, type AffiliateUser } from './AffiliateSidebar';
import '@/styles/affiliate-theme.css';
import { 
  Menu, 
  Bell, 
  HelpCircle
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// ============================================
// TYPES
// ============================================

interface AffiliateLayoutProps {
  children: React.ReactNode;
  /** Optionaler Page Title */
  pageTitle?: string;
  /** Optionaler Subtitle */
  pageSubtitle?: string;
  /** Optionale Actions im Header */
  headerActions?: React.ReactNode;
}

// ============================================
// CONSTANTS
// ============================================

// Route → Title Mapping
const ROUTE_TITLES: Record<string, { title: string; subtitle?: string }> = {
  '/affiliate': { title: 'Dashboard', subtitle: 'Übersicht Ihrer Affiliate-Aktivitäten' },
  '/affiliate/leads': { title: 'Leads', subtitle: 'Verwalten Sie Ihre Kontakte' },
  '/affiliate/lokale': { title: 'Meine Lokale', subtitle: 'Ihre vermittelten Restaurants' },
  '/affiliate/provisionen': { title: 'Provisionen', subtitle: 'Ihre Einnahmen im Überblick' },
  '/affiliate/konstantleiter': { title: 'Konstantleiter', subtitle: 'Bonusprogramm & Level' },
  '/affiliate/marketing': { title: 'Marketing', subtitle: 'Werbematerialien & Assets' },
  '/affiliate/zahlungen': { title: 'Zahlungen', subtitle: 'Auszahlungsverlauf' },
  '/affiliate/einstellungen': { title: 'Einstellungen', subtitle: 'Profil & Präferenzen' },
  '/affiliate/support': { title: 'Support', subtitle: 'Hilfe & FAQ' },
};

// Mock User - später aus Auth Context
const MOCK_USER: AffiliateUser = {
  name: 'Martin Klein',
  email: 'martin@beispiel.de',
  level: 'gold',
  avatar: undefined,
};

// ============================================
// SUB-COMPONENTS
// ============================================

interface TopbarProps {
  title: string;
  subtitle?: string;
  onMenuClick?: () => void;
  showMenu?: boolean;
  actions?: React.ReactNode;
}

const Topbar: React.FC<TopbarProps> = ({ 
  title, 
  subtitle, 
  onMenuClick, 
  showMenu = false,
  actions 
}) => {
  return (
    <header className={cn(
      'sticky top-0 z-30',
      'bg-white/70 backdrop-blur-xl',
      'border-b border-white/50',
      'px-3 sm:px-4 md:px-6 py-3 sm:py-4',
      'shadow-[0_1px_3px_rgba(0,0,0,0.04)]',
      'safe-area-inset-top' // iOS safe area
    )}>
      <div className="flex items-center justify-between gap-2 sm:gap-4">
        {/* Left: Menu + Title */}
        <div className="flex items-center gap-3 min-w-0">
          {/* Mobile Menu Button - Min 44px touch target */}
          {showMenu && (
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden shrink-0 h-11 w-11 min-h-[44px] min-w-[44px] p-0 rounded-xl"
              onClick={onMenuClick}
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          
          {/* Title Area */}
          <div className="min-w-0">
            <h1 className="text-lg md:text-xl font-semibold text-gray-900 truncate">
              {title}
            </h1>
            {subtitle && (
              <p className="text-sm text-gray-500 hidden sm:block truncate">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        
        {/* Right: Actions + Notifications */}
        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {actions}
          
          {/* Notifications - Min 44px touch target */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="relative h-11 w-11 min-h-[44px] min-w-[44px] p-0 rounded-xl">
                <Bell className="h-5 w-5 text-gray-600" />
                <span className="absolute top-1 right-1 w-4 h-4 bg-emerald-500 text-[10px] text-white rounded-full flex items-center justify-center font-medium">
                  3
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <div className="p-3 border-b">
                <h3 className="font-semibold">Benachrichtigungen</h3>
              </div>
              <DropdownMenuItem className="p-3 cursor-pointer">
                <div>
                  <p className="text-sm font-medium">Neuer Lead eingegangen</p>
                  <p className="text-xs text-gray-500">vor 5 Minuten</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem className="p-3 cursor-pointer">
                <div>
                  <p className="text-sm font-medium">Auszahlung eingeleitet</p>
                  <p className="text-xs text-gray-500">vor 2 Stunden</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="p-3 text-center text-primary cursor-pointer">
                Alle anzeigen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Help - Min 44px touch target */}
          <Button variant="ghost" size="sm" className="h-11 w-11 min-h-[44px] min-w-[44px] p-0 rounded-xl hidden sm:flex">
            <HelpCircle className="h-5 w-5 text-gray-600" />
          </Button>
        </div>
      </div>
    </header>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

export const AffiliateLayout: React.FC<AffiliateLayoutProps> = ({
  children,
  pageTitle,
  pageSubtitle,
  headerActions,
}) => {
  const [location, setLocation] = useLocation();
  const { logoutMutation } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Get title from route or props
  const routeInfo = ROUTE_TITLES[location] || { title: 'Affiliate Portal' };
  const title = pageTitle || routeInfo.title;
  const subtitle = pageSubtitle || routeInfo.subtitle;
  
  // Navigation handler
  const handleNavigate = useCallback((route: string) => {
    setLocation(route);
    setIsMobileMenuOpen(false);
  }, [setLocation]);
  
  // Logout handler — uses real auth mutation
  const handleLogout = useCallback(() => {
    logoutMutation.mutate(undefined, {
      onSuccess: () => setLocation('/'),
    });
  }, [logoutMutation, setLocation]);
  
  return (
    <div className="min-h-screen affiliate-bg-animated">
      {/* Desktop Sidebar */}
      <div className="hidden md:block fixed inset-y-0 left-0 z-40">
        <AffiliateSidebar
          activeRoute={location}
          isCollapsed={isSidebarCollapsed}
          user={MOCK_USER}
          onNavigate={handleNavigate}
          onCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          onLogout={handleLogout}
        />
      </div>
      
      {/* Mobile Sidebar (Sheet/Drawer) */}
      <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
        <SheetContent side="left" className="p-0 w-[280px]">
          <AffiliateSidebar
            activeRoute={location}
            isCollapsed={false}
            user={MOCK_USER}
            onNavigate={handleNavigate}
            onLogout={handleLogout}
          />
        </SheetContent>
      </Sheet>
      
      {/* Main Content Area */}
      <div className={cn(
        'transition-[margin-left] duration-300 ease-in-out',
        // Desktop: Offset für Sidebar
        isSidebarCollapsed ? 'md:ml-[72px]' : 'md:ml-[280px]',
      )}>
        {/* Topbar */}
        <Topbar
          title={title}
          subtitle={subtitle}
          onMenuClick={() => setIsMobileMenuOpen(true)}
          showMenu={true}
          actions={headerActions}
        />
        
        {/* Page Content */}
        <main className="p-3 sm:p-4 md:p-6 lg:p-8 pb-safe">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AffiliateLayout;
